import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import UrgencyBar from '@/components/feature/UrgencyBar';
import MobileStickyBar from '@/components/feature/MobileStickyBar';

const PROJECT_TYPES = [
  'Hardwood Flooring Installation',
  'LVP / Vinyl Plank Installation',
  'Laminate Flooring Installation',
  'Engineered Wood Installation',
  'Dustless Floor Removal',
  'Tile Removal',
  'Subfloor Preparation & Leveling',
  'Bathroom Remodeling',
  'Full Renovation Project',
  'Other',
];

const WA_MESSAGE = encodeURIComponent(
  'Olá! / Hi! I found VM Construction online and I\'d like a FREE estimate.\n\nProject type: ___\nLocation (city): ___\nApprox. sq ft: ___\n\nI can also send photos!'
);

const CONTACT_INFO = [
  { icon: 'ri-whatsapp-line', label: 'WhatsApp (Fastest)', value: 'Message us — reply in minutes', href: `https://wa.me/19044019383?text=${WA_MESSAGE}`, highlight: true },
  { icon: 'ri-phone-line', label: 'Call Us', value: '(904) 401-9383', href: 'tel:+19044019383', highlight: false },
  { icon: 'ri-mail-line', label: 'Email Us', value: 'vmofficeconstruction@gmail.com', href: 'mailto:vmofficeconstruction@gmail.com', highlight: false },
  { icon: 'ri-map-pin-line', label: 'Service Area', value: 'Tampa Bay & Sarasota, FL', href: null, highlight: false },
  { icon: 'ri-time-line', label: 'Working Hours', value: 'Mon – Sat: 7am – 6pm', href: null, highlight: false },
];

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const [descError, setDescError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = (form.elements.namedItem('description') as HTMLTextAreaElement).value;
    if (desc.length > 500) {
      setDescError('Description must be 500 characters or less.');
      return;
    }
    setDescError('');
    setStatus('loading');

    const data = new URLSearchParams();
    const fields = ['name', 'phone', 'email', 'project_type', 'description'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el) data.append(field, el.value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d76ritrq7u9ee9hcekgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <UrgencyBar />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d1fb531a4a35b300a3a4933ed8727607.jpg"
            alt="Contact VM General Construction Tampa Bay"
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/80 via-[#0d0d0d]/60 to-[#0d0d0d]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">Get In Touch</p>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-5">
            Request Your <span className="text-[#c9a55a]">Free Estimate</span>
          </h1>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            The more details you share, the more accurate your estimate will be.
            We respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* Left — Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl p-8">
              <h2 className="text-white font-black text-xl mb-6">Contact Information</h2>
              <div className="flex flex-col gap-4">
                {CONTACT_INFO.map((item) => (
                  <div
                    key={item.label}
                    className={`flex items-start gap-4 rounded-xl p-3 transition-colors ${
                      item.highlight ? 'bg-[#25D366]/10 border border-[#25D366]/25' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                      item.highlight ? 'bg-[#25D366]/20' : 'bg-[#c9a55a]/10'
                    }`}>
                      <i className={`${item.icon} text-lg ${item.highlight ? 'text-[#25D366]' : 'text-[#c9a55a]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs uppercase tracking-wider mb-1 ${item.highlight ? 'text-[#25D366]/70 font-bold' : 'text-white/40'}`}>
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.highlight ? '_blank' : undefined}
                          rel={item.highlight ? 'nofollow noopener noreferrer' : undefined}
                          className={`font-medium text-sm transition-colors cursor-pointer truncate block ${
                            item.highlight
                              ? 'text-[#25D366] hover:text-[#1ebe5d]'
                              : 'text-white hover:text-[#c9a55a]'
                          }`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white font-medium text-sm">{item.value}</p>
                      )}
                    </div>
                    {item.highlight && (
                      <div className="flex-shrink-0 bg-[#25D366] text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide">
                        Fast
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Why Contact Us */}
            <div className="bg-[#111111] border border-white/8 rounded-2xl p-8">
              <h3 className="text-white font-bold text-base mb-5">Why Get an Estimate?</h3>
              {[
                'No obligation — 100% free',
                'Accurate pricing for your project',
                'On-site assessment when needed',
                'Detailed scope of work included',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 mb-4 last:mb-0">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-[#c9a55a] text-base" />
                  </div>
                  <span className="text-white/60 text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Service Area Map Placeholder */}
            <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl overflow-hidden h-52 relative">
              <iframe
                title="VM General Construction Service Area Tampa Bay"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d226778.24715893!2d-82.5785143!3d27.9942105!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2b782b3b9d1e1%3A0xa75f1389af96b463!2sTampa%2C%20FL!5e0!3m2!1sen!2sus!4v1700000000000"
                className="w-full h-full border-0"
                allowFullScreen
                loading="lazy"
              />
              <div className="absolute bottom-3 left-3 bg-[#0d0d0d]/90 backdrop-blur-sm rounded-lg px-3 py-2">
                <p className="text-white text-xs font-semibold">Tampa Bay &amp; Sarasota, FL</p>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl p-8 md:p-10">

              {/* Success State */}
              {status === 'success' && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#c9a55a]/15 rounded-full mb-6">
                    <i className="ri-checkbox-circle-line text-[#c9a55a] text-4xl" />
                  </div>
                  <h3 className="text-white font-black text-2xl mb-3">Request Sent!</h3>
                  <p className="text-white/55 text-base max-w-sm leading-relaxed mb-8">
                    Thank you! We&apos;ll review your project details and get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="inline-flex items-center gap-2 bg-[#c9a55a]/15 hover:bg-[#c9a55a]/25 border border-[#c9a55a]/30 text-[#c9a55a] font-semibold px-6 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" />
                    Submit Another Request
                  </button>
                </div>
              )}

              {status !== 'success' && (
                <>
                  <h2 className="text-white font-black text-2xl mb-2">Tell Us About Your Project</h2>
                  <p className="text-white/40 text-sm mb-8">Fill in the details below and we&apos;ll get back to you shortly.</p>

                  <form
                    ref={formRef}
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    {/* Name + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-white/50 text-xs uppercase tracking-wider mb-2" htmlFor="name">
                          Full Name <span className="text-[#c9a55a]">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          placeholder="John Smith"
                          className="w-full bg-[#111111] border border-white/10 focus:border-[#c9a55a]/60 text-white placeholder-white/25 text-sm rounded-xl px-4 py-3 outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-white/50 text-xs uppercase tracking-wider mb-2" htmlFor="phone">
                          Phone Number <span className="text-[#c9a55a]">*</span>
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          placeholder="(904) 000-0000"
                          className="w-full bg-[#111111] border border-white/10 focus:border-[#c9a55a]/60 text-white placeholder-white/25 text-sm rounded-xl px-4 py-3 outline-none transition-colors"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2" htmlFor="email">
                        Email Address <span className="text-[#c9a55a]">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="john@email.com"
                        className="w-full bg-[#111111] border border-white/10 focus:border-[#c9a55a]/60 text-white placeholder-white/25 text-sm rounded-xl px-4 py-3 outline-none transition-colors"
                      />
                    </div>

                    {/* Project Type */}
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2" htmlFor="project_type">
                        Project Type <span className="text-[#c9a55a]">*</span>
                      </label>
                      <div className="relative">
                        <select
                          id="project_type"
                          name="project_type"
                          required
                          defaultValue=""
                          className="w-full bg-[#111111] border border-white/10 focus:border-[#c9a55a]/60 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors appearance-none cursor-pointer"
                        >
                          <option value="" disabled className="text-white/30">Select your project type...</option>
                          {PROJECT_TYPES.map((type) => (
                            <option key={type} value={type} className="text-white bg-[#1a1a1a]">{type}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                          <i className="ri-arrow-down-s-line text-white/40 text-base" />
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-white/50 text-xs uppercase tracking-wider" htmlFor="description">
                          Project Description
                        </label>
                        <span className={`text-xs ${charCount > 450 ? 'text-[#c9a55a]' : 'text-white/30'}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        id="description"
                        name="description"
                        maxLength={500}
                        rows={5}
                        placeholder="Describe your project — floor type, square footage, current condition, timeline, etc. The more detail, the better!"
                        onChange={(e) => {
                          setCharCount(e.target.value.length);
                          if (e.target.value.length <= 500) setDescError('');
                        }}
                        className="w-full bg-[#111111] border border-white/10 focus:border-[#c9a55a]/60 text-white placeholder-white/25 text-sm rounded-xl px-4 py-3 outline-none transition-colors resize-none"
                      />
                      {descError && <p className="text-red-400 text-xs mt-1">{descError}</p>}
                      <p className="text-white/30 text-xs mt-2">
                        Pro tip: Include your city, floor type, and approximate square footage for a faster estimate.
                      </p>
                    </div>

                    {/* WhatsApp Alternative */}
                    <div className="flex items-center justify-between gap-4 bg-[#25D366]/8 border border-[#25D366]/20 rounded-xl px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <i className="ri-whatsapp-line text-[#25D366] text-xl" />
                        </div>
                        <div>
                          <p className="text-white/80 text-sm font-bold leading-tight">Prefer WhatsApp?</p>
                          <p className="text-white/45 text-xs mt-0.5">Send photos &amp; get a faster reply — Falamos português também!</p>
                        </div>
                      </div>
                      <a
                        href={`https://wa.me/19044019383?text=${WA_MESSAGE}`}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="flex-shrink-0 flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-xs px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-sm" />
                        Chat Now
                      </a>
                    </div>

                    {/* Photo Upload Note */}
                    <div className="flex items-start gap-3 bg-[#c9a55a]/8 border border-[#c9a55a]/20 rounded-xl px-4 py-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-image-line text-[#c9a55a] text-base" />
                      </div>
                      <p className="text-white/50 text-xs leading-relaxed">
                        <strong className="text-white/70">Have photos?</strong> Send images of your project area via WhatsApp for a more accurate estimate.
                      </p>
                    </div>

                    {/* Error State */}
                    {status === 'error' && (
                      <div className="flex items-center gap-3 bg-red-900/20 border border-red-900/40 rounded-xl px-4 py-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line text-red-400 text-base" />
                        </div>
                        <p className="text-red-400 text-sm">Something went wrong. Please try again or call us directly.</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full flex items-center justify-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] disabled:opacity-60 disabled:cursor-not-allowed text-[#0d0d0d] font-black text-base py-4 rounded-xl transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer mt-2"
                    >
                      {status === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-lg" />
                          Sending Request...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill text-lg" />
                          Send Request — Get Free Estimate
                        </>
                      )}
                    </button>

                    <p className="text-white/25 text-xs text-center">
                      By submitting, you agree to be contacted by VM General Construction LLC regarding your project.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Trust Bar */}
      <section className="bg-[#111111] border-t border-white/8 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'ri-shield-check-line', title: 'Fully Insured', desc: 'Licensed and insured for your peace of mind.' },
              { icon: 'ri-time-line', title: 'Fast Response', desc: 'We respond to all estimates within 24 hours.' },
              { icon: 'ri-map-pin-line', title: 'Local Experts', desc: 'Serving Tampa Bay & Sarasota communities.' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#c9a55a]/10 rounded-xl">
                  <i className={`${item.icon} text-[#c9a55a] text-xl`} />
                </div>
                <h3 className="text-white font-bold text-base">{item.title}</h3>
                <p className="text-white/45 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
